import React, { useEffect, useState } from "react";
import { useModalContext } from "../../context/ModalContext";
import BuilderModal from "../../Layouts/Modal/BuilderModal";
import Button from "../FormComponents/Button";
import Input from "../FormComponents/Input";
import MultiRadio from "../FormComponents/MultiRadio";
import SingleSelectBox from "../FormComponents/SingleSelectBox";
import { getToolTipAttributes } from "../../utils/tooltip";
import MaterialIcon from "../BuilderComponents/MaterialIcon";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../services/axiosService";

const ProductionCard = ({ title, appData, setAppData }) => {
  const { getUserConfirmationForDelete } = useModalContext();
  const [isVersionOpen, setIsVersionOpen] = useState("");
  const [isDeployOpen, setIsDeployOpen] = useState("");

  // set app Versions
  const SetAppVersion = ({ id, version, cluster, onSave }) => {
    const [appVersions, setAppVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState("");
    const [selectedCluster, setSelectedCluster] = useState(cluster);

    const fetchAppVersions = async () => {
      try {
        const { data } = await axiosInstance.get(`/application/version`, {
          headers: {
            "Zino-App-Id": id
          }
        });
        setAppVersions(data);
        getSelectedVersion(data);
      } catch (error) {
        handleApiError(error);
      }
    };

    const handleUpdateVersion = (obj) => {
      setSelectedVersion(obj.id);
    };
    const getSelectedVersion = (data) => {
      const versionObj = data?.find((appversion) => appversion?.version === version);
      setSelectedVersion(versionObj?.id);
    };
    useEffect(() => {
      fetchAppVersions();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="builder_form">
        <SingleSelectBox
          selectedData={selectedVersion}
          options={appVersions}
          labelKey={["version"]}
          onChange={(obj) => handleUpdateVersion(obj)}
          label="Application version"
          placeholder="Select Version"
          isSearch={true}
        />
        <MultiRadio
          label="Select Cluster"
          options={[
            { id: "kube", name: "kube" },
            { id: "machine", name: "machine" }
          ]}
          selectedValue={selectedCluster}
          onChange={(e) => setSelectedCluster(e.target.value)}
        />
        <Button
          onClick={() =>
            onSave(
              selectedCluster,
              selectedVersion,
              appVersions?.find((appversion) => appversion?.id === selectedVersion).version
            )
          }
        >
          Set version
        </Button>
      </div>
    );
  };
  const updateApplicationVersion = async (cluster, version, versionName) => {
    if (!version) {
      toastError("Select a version");
    }
    let url;
    if (isVersionOpen === "prod") {
      url = `/application/version/${version}`;
    }
    if (isVersionOpen === "stage") {
      url = `/application/staging/version/${version}`;
    }
    const clusterPayload = { cluster: cluster };
    try {
      const { message } = await axiosInstance.put(url, clusterPayload, {
        headers: {
          "Zino-App-Id": appData.id
        }
      });

      setAppData((appData) => {
        return {
          ...appData,
          ...(isVersionOpen === "prod"
            ? { version: versionName, cluster: cluster }
            : { staging_version: versionName, staging_cluster: cluster })
        };
      });

      setIsVersionOpen("");
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };
  const closeVersionModal = () => {
    setIsVersionOpen("");
  };

  // Start app actions
  const createAction = async (type, id) => {
    let url;
    if (type === "stage") {
      url = `/application/staging-infra`;
    } else if (type === "prod") {
      url = `/application/infra`;
    }
    const response = await axiosInstance.post(url, null, {
      headers: {
        "Zino-App-Id": id
      }
    });
    setAppData({
      ...appData,
      [type === "stage" ? "staging_status" : "status"]: "starting"
    });
    toastSuccess(response.message);
  };

  const createApplicationLink = async (type, id) => {
    try {
      await createAction(type, id);
    } catch (error) {
      handleApiError(error);
    }
  };

  // Deployment actions
  const deployAction = async (type, id, comment) => {
    let url;
    if (type === "stage") {
      url = `/application/deploy/staging-env`;
    } else if (type === "prod") {
      url = `/application/deploy/staging`;
    }
    try {
      const response = await axiosInstance.post(
        url,
        { comment }, // Add comment to request body
        {
          headers: {
            "Zino-App-Id": id
          }
        }
      );
      toastSuccess(response.message);
      setIsDeployOpen(""); // Close modal after successful deployment
    } catch (error) {
      handleApiError(error);
      console.error("Error deploying application:", error);
    }
  };

  const deployInProduction = async (type) => {
    setIsDeployOpen(type);
  };

  // Destroy actions
  const destroyAction = async (type, id, name) => {
    const userConfirmed = await getUserConfirmationForDelete(name, "", "stop");
    if (!userConfirmed) return;

    let url;
    if (type === "stage") {
      url = `/application/staging-infra`;
    } else if (type === "prod") {
      url = `/application/infra`;
    }
    const response = await axiosInstance.delete(url, {
      headers: {
        "Zino-App-Id": id
      }
    });
    setAppData({
      ...appData,
      [type === "stage" ? "staging_status" : "status"]: "stopping"
    });
    toastSuccess(response.message);
  };

  const destroyApplicationLink = async (type, id, name) => {
    try {
      await destroyAction(type, id, name);
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <div className="setting_card">
      <div className="setting_card_header">
        {title && <div className="card-title setting_card_title">{title}</div>}
        <div className="setting_card_actions_container">
          <div>
            {appData.status === "live" && (
              <Button
                variant="secondary"
                size="small"
                className="card-button openapp"
                onClick={() => window.open(appData.web_url, "blank")}
                {...getToolTipAttributes("Open Link")}
              >
                <MaterialIcon name="link" className="action font_size_large custom-icon" />
              </Button>
            )}
          </div>
          <Button
            variant="secondary"
            size="small"
            className="card-button edit"
            onClick={() => setIsVersionOpen("prod")}
            {...getToolTipAttributes("Software Version")}
          >
            <MaterialIcon name="edit_note" className="action font_size_large custom-icon" />
          </Button>
          <Button
            variant="primary"
            size="small"
            className="card-button"
            onClick={() => deployInProduction("prod", appData.id)}
            {...getToolTipAttributes("Deploy")}
          >
            <MaterialIcon name="rocket_launch" className="action font_size_large" />
          </Button>
        </div>
      </div>
      <div className="card-body pb_3 p_0">
        <div className="mb_2 font_weight_medium setting_card_info_label">
          Software Version:{" "}
          <span className="font_weight_semibold setting_card_info_value">{appData.version || "N/A"}</span>
        </div>
        <div className="mb_2 font_weight_medium setting_card_info_label">
          Cluster: <span className="font_weight_semibold setting_card_info_value">{appData.cluster || "N/A"}</span>
        </div>
        <div className="mb_0 font_weight_medium setting_card_info_label">
          Application Version:{" "}
          <span className="font_weight_semibold setting_card_info_value">v.{appData.checkpoint || "0"}</span>
        </div>
      </div>
      <div className="card-footer d_flex justify_between align_center border_top pt_3">
        <div className={`app_status ${appData?.status?.toLowerCase()} setting_app_status_badge`}>
          {appData?.status || "Unknown"}
        </div>
        {appData.status === "stopped" && (
          <Button
            size="small"
            variant="secondary"
            onClick={() => createApplicationLink("prod", appData.id)}
            isLoading={appData.status === "starting"}
          >
            Start
          </Button>
        )}
        {appData.status === "live" && (
          <Button
            size="small"
            variant="secondary"
            onClick={() => destroyApplicationLink("prod", appData.id, appData.name)}
            isLoading={appData.status === "starting"}
          >
            Stop
          </Button>
        )}
      </div>
      <BuilderModal isOpen={isVersionOpen} onClose={closeVersionModal} modalName="Set Software Version">
        {isVersionOpen === "prod" && (
          <SetAppVersion
            id={appData.id}
            version={appData?.version}
            cluster={appData?.cluster}
            onSave={updateApplicationVersion}
          />
        )}
      </BuilderModal>

      <BuilderModal
        isOpen={isDeployOpen}
        onClose={() => setIsDeployOpen("")}
        modalName="Are you sure you want to deploy in production?"
        headerStyle={{ gap: "10px" }}
      >
        {isDeployOpen && (
          <DeployAppVersion appId={appData.id} onSave={(comment) => deployAction(isDeployOpen, appData.id, comment)} />
        )}
      </BuilderModal>
    </div>
  );
};

const DeployAppVersion = ({ onSave }) => {
  const [comment, setComment] = useState("");

  return (
    <div className="builder_form">
      <Input
        placeholder="Enter deployment comment"
        label="Comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        required
      />
      <Button onClick={() => onSave(comment)} disabled={!comment}>
        Deploy
      </Button>
    </div>
  );
};

export default ProductionCard;
